import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DocumentPreviewService, targetStateData, ToastService } from '@isophi/core-ui';
import { PortfolioManagerReportsService } from '@isophi/isophi-pdfs-api';
import { ManagerTarget, PapCategoriesService, PortfolioService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { map, takeUntil } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { HelperService } from '../../../../core/services/helper.service';
import { BaseComponent } from '../../../../shared/components/base/base.component';
import { supportTypeData } from '../../constants/data';
import { PortfolioTargetDetailForm } from '../../constants/forms';

@Component({
  selector: 'app-pap-plan-detail',
  templateUrl: './pap-plan-detail.component.html',
  styleUrls: ['./pap-plan-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PapPlanDetailComponent extends BaseComponent implements OnInit {
  @Input() portfolioTargetId: string;

  portfolioTarget: ManagerTarget;

  loadingPortfolioTarget = true;

  form = new FormGroup<PortfolioTargetDetailForm>({
    description: new FormControl<string | null>(null),
    competenceUuids: new FormControl<string[]>([]),
  });

  materialServer = environment.materialServer;

  targetStateData = targetStateData;

  supportTypeData = supportTypeData;

  private readonly route = inject(ActivatedRoute);

  private readonly routerLinkFactory = inject(RouterLinkFactory);

  private readonly portfolioService = inject(PortfolioService);

  private readonly papCategoriesService = inject(PapCategoriesService);

  private readonly portfolioManagerReportsService = inject(PortfolioManagerReportsService);

  private readonly toastService = inject(ToastService);

  private readonly translocoService = inject(TranslocoService);

  private readonly helperService = inject(HelperService);

  private readonly spinner = inject(NgxSpinnerService);

  private readonly cd = inject(ChangeDetectorRef);

  private readonly documentPreviewService = inject(DocumentPreviewService);

  fetchCategories = () =>
    this.papCategoriesService.papCategoriesList(undefined, undefined, undefined, 1000).pipe(map((categories) => categories.results));

  fetchPortfolioActivityTargetLinks = (portfolioTargetId: string) =>
    this.portfolioService
      .portfolioActivityTargetLinksList(undefined, undefined, undefined, undefined, undefined, undefined, portfolioTargetId)
      .pipe(map((links) => links.results));

  fetchPortfolioActivities = () => this.portfolioService.portfolioActivitiesList().pipe(map((activities) => activities.results));

  buildPortfolioActivityRouteLink = (portfolioActivityId: string) => this.routerLinkFactory.papPortfolioDetail(portfolioActivityId);

  ngOnInit(): void {
    this.portfolioTargetId = this.route.snapshot.params['id'];
    this.loadPortfolioTarget();
  }

  loadPortfolioTarget(): void {
    this.loadingPortfolioTarget = true;
    this.portfolioService
      .portfolioTargetsRetrieve(this.portfolioTargetId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (portfolioTarget) => {
          this.portfolioTarget = portfolioTarget;
          this.helperService.setTitle(null, portfolioTarget.title);
          this._fillPortfolioTargetForm();
          this.loadingPortfolioTarget = false;
        },
        error: () => {
          this.loadingPortfolioTarget = false;
          this.toastService.error(this.translocoService.translate('pap/plan/loadErrorToast'));
        },
      });
  }

  private _fillPortfolioTargetForm(): void {
    if (!this.portfolioTarget) {
      return;
    }

    this.form.patchValue({
      description: this.portfolioTarget.description,
      competenceUuids: this.portfolioTarget.competences.map((competence) => competence.uuid),
    });

    this.cd.markForCheck();
  }

  printPage(): void {
    this.spinner.show();
    this.portfolioManagerReportsService
      .getManagerTargetDetailReport({ targetId: this.portfolioTargetId })
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (data: Blob) => {
          this.documentPreviewService.openBlob(data, `${this.translocoService.translate('file/targetReportDetail')}.pdf`);
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('pap/plan/printManagerTargetReportErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  getTargetStateClass(state?: string): string {
    switch (state) {
      case 'waiting':
        return 'target-state-waiting';
      case 'in_process':
        return 'target-state-in-process';
      case 'finished':
        return 'target-state-finished';
      case 'incomplete':
        return 'target-state-incomplete';
      default:
        return '';
    }
  }
}
